// components/loader/GlobalLoader.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchHomeData } from "../../store/slices/homeSlice";
import { openCityModal } from "../../store/slices/citySlice";
import {
  FiRefreshCw,
  FiXCircle,
  FiZap,
  FiShield,
  FiClock,
} from "react-icons/fi";

let loaderCount = 0;

export const showGlobalLoader = () => {
  loaderCount++;
  const event = new CustomEvent("showGlobalLoader");
  window.dispatchEvent(event);
};

export const hideGlobalLoader = () => {
  loaderCount--;

  if (loaderCount <= 0) {
    loaderCount = 0;
    const event = new CustomEvent("hideGlobalLoader");
    window.dispatchEvent(event);
  }
};

export const resetLoader = () => {
  loaderCount = 0;
  const event = new CustomEvent("hideGlobalLoader");
  window.dispatchEvent(event);
};

const GlobalLoader = () => {
  const dispatch = useDispatch();

  const { loading, error, homeData } = useSelector((state) => state.home);
  const { isCityChanging, selectedCity } = useSelector(
    (state) => state.city,
  );

  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  /*
   * CITY_ID_REQUIRED is an expected application state.
   *
   * It means the application cannot load city-dependent data until
   * the user selects a city. It must never be shown as a global
   * error screen.
   */
  const isCityRequiredError =
    error === "City selection is required" ||
    error?.error_code === "CITY_ID_REQUIRED" ||
    error?.code === "CITY_ID_REQUIRED";

  /*
   * Handle loader visibility from events.
   */
  useEffect(() => {
    const showLoader = () => setVisible(true);
    const hideLoader = () => setVisible(false);

    window.addEventListener("showGlobalLoader", showLoader);
    window.addEventListener("hideGlobalLoader", hideLoader);

    return () => {
      window.removeEventListener("showGlobalLoader", showLoader);
      window.removeEventListener("hideGlobalLoader", hideLoader);
    };
  }, []);

  /*
   * Fetch home data on mount only when a city has already been selected.
   *
   * When there is no city, the CitySelectorModal is responsible for
   * asking the user to select one.
   */
  useEffect(() => {
    if (!selectedCity?.id) {
      dispatch(openCityModal());
      setVisible(false);
      return;
    }

    dispatch(fetchHomeData());
  }, [dispatch, selectedCity?.id]);

  /*
   * When backend/application tells us that a city is required,
   * open the city selector instead of displaying the global error.
   */
  useEffect(() => {
    if (isCityRequiredError) {
      setVisible(false);
      setProgress(0);
      dispatch(openCityModal());
    }
  }, [dispatch, isCityRequiredError]);

  /*
   * Update progress.
   */
  useEffect(() => {
    /*
     * City selection is required, so there is nothing to load yet.
     */
    if (isCityRequiredError) {
      setVisible(false);
      setProgress(0);
      return;
    }

    if (!loading && homeData) {
      setProgress(100);
      setIsInitialized(true);

      const timeout = setTimeout(() => {
        if (!isCityChanging && !loading) {
          hideGlobalLoader();
        }
      }, 500);

      return () => clearTimeout(timeout);
    }

    if (loading || isCityChanging) {
      setVisible(true);
      setProgress(0);

      const interval = setInterval(() => {
        setProgress((prev) => (prev >= 90 ? 90 : prev + 2));
      }, 50);

      return () => clearInterval(interval);
    }
  }, [
    loading,
    homeData,
    isCityChanging,
    isCityRequiredError,
  ]);

  /*
   * Hide loader when initialized and there is no error and no city change.
   */
  useEffect(() => {
    if (
      isInitialized &&
      !error &&
      homeData &&
      !isCityChanging &&
      !loading
    ) {
      hideGlobalLoader();
    }
  }, [
    isInitialized,
    error,
    homeData,
    isCityChanging,
    loading,
  ]);

  /*
   * CITY_ID_REQUIRED is not a global error.
   *
   * The CitySelectorModal handles this state.
   */
  if (isCityRequiredError) {
    return null;
  }

  /*
   * Don't show if not visible and no error.
   */
  if (!visible && !error) {
    return null;
  }

  /*
   * Error state.
   *
   * Only real/unexpected errors reach this screen.
   */
  if (error) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-xs p-5 bg-white shadow-xl dark:bg-gray-800 rounded-xl">
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-red-50 dark:bg-red-900/20">
              <FiXCircle className="w-6 h-6 text-red-500" />
            </div>

            <img
              src="https://app.tap2cash.in/assets/logo.png"
              alt="Tap2Cash"
              className="object-contain w-8 h-8 mb-2"
            />

            <p className="mb-3 text-xs text-gray-600 dark:text-gray-400">
              {error}
            </p>

            <button
              className="flex items-center justify-center w-full gap-2 px-3 py-2 text-xs font-medium text-white transition-colors bg-orange-500 rounded-lg hover:bg-orange-600"
              onClick={() => {
                dispatch(fetchHomeData());
                setProgress(0);
              }}
            >
              <FiRefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  /*
   * Loading state.
   */
  return (
    <div className="fixed inset-0 z-[9999] bg-white dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-[260px] text-center">
        {/* Logo */}
        <div className="mb-4">
          <img
            src="https://app.tap2cash.in/assets/logo.png"
            alt="Tap2Cash"
            className="object-contain w-12 h-12 mx-auto mb-2"
          />

          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Tap2Cash
          </h2>
        </div>

        {/* Progress Ring */}
        <div className="relative w-20 h-20 mx-auto mb-3">
          <svg className="w-20 h-20 transform -rotate-90">
            <circle
              cx="40"
              cy="40"
              r="36"
              stroke="#e5e7eb"
              strokeWidth="2.5"
              fill="none"
            />

            <circle
              cx="40"
              cy="40"
              r="36"
              stroke="#f97316"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 36}`}
              strokeDashoffset={`${
                2 * Math.PI * 36 * (1 - progress / 100)
              }`}
              className="transition-all duration-300"
            />
          </svg>

          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-bold text-orange-500">
              {progress}%
            </span>
          </div>
        </div>

        {/* Status Text */}
        <p className="mb-3 text-xs font-medium text-gray-600 dark:text-gray-400">
          {loading || isCityChanging ? "Loading..." : "Ready!"}
        </p>

        {/* Progress Bar */}
        <div className="h-0.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-4">
          <div
            className="h-full transition-all duration-300 bg-orange-500 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Features */}
        <div className="flex justify-center gap-4 text-[10px] text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <FiZap className="w-3 h-3 text-orange-500" />
            <span>Instant</span>
          </div>

          <div className="flex items-center gap-1">
            <FiShield className="w-3 h-3 text-green-500" />
            <span>Secure</span>
          </div>

          <div className="flex items-center gap-1">
            <FiClock className="w-3 h-3 text-blue-500" />
            <span>24/7</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalLoader;